import { MantineProvider } from "@mantine/core";
import { Notifications } from "@mantine/notifications";
import { ModalsProvider } from "@mantine/modals";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { BrowserRouter } from "react-router-dom";
import * as Sentry from "@sentry/react";
import { AppRoutes } from "./routes";
import { theme } from "./theme/theme";
import { SentryAuthSync } from "./components/SentryAuthSync";
import { CsrfInitializer } from "./components/CsrfInitializer";
import { NativeSessionGuard } from "./components/NativeSessionGuard";
import { DesktopAppEvents } from "./components/DesktopAppEvents";
import { AppUpdateRequiredGate } from "./components/AppUpdateRequiredGate";
import { AssumedRoleProvider } from "./contexts/AssumedRoleContext";
import { isChunkLoadError, reloadOnceForStaleChunk } from "./lib/chunkReload";
import { createAppMutationCache } from "./lib/mutationCache";
import { createAppQueryCache } from "./lib/queryCache";

// Mantine styles
import "@mantine/core/styles.css";
import "@mantine/notifications/styles.css";
import "@mantine/dates/styles.css";
import "@mantine/carousel/styles.css";

// Global dark mode overrides
import "./styles/global.css";
// Enhanced calendar styles
import "./styles/calendar.css";
// Touch-device affordances (iPad shell) — every rule gated on pointer: coarse
import "./styles/touch.css";

// Create React Query client
const queryClient = new QueryClient({
  // US-081: every rejected mutation pushes one error toast unless its meta
  // opts out (FORM_MUTATION_META) — no per-hook onError needed for that.
  mutationCache: createAppMutationCache(),
  // Audit B-H1: global safety net so a failed GET can't silently render as an
  // empty list/detail. Skips 401/404; toasts 403/5xx/network (deduped).
  queryCache: createAppQueryCache(),
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function App() {
  return (
    <Sentry.ErrorBoundary
      fallback={({ error, resetError }) => {
        // Stale-chunk failures (a deploy replaced the chunks this tab loaded
        // against) get a dedicated recovery path — never the generic crash
        // screen. Auto-reload once (cooldown-guarded) to pull fresh index.html +
        // current chunks; if the cooldown already fired during an active
        // rollout, offer an explicit FULL reload. resetError is wrong here — it
        // just retries the same missing chunk, which keeps failing until the
        // user manually refreshes (SCRUM-111).
        if (isChunkLoadError(error)) {
          if (reloadOnceForStaleChunk()) {
            return (
              <div style={{ padding: "2rem", textAlign: "center" }}>
                <p>Updating to the latest version…</p>
              </div>
            );
          }
          return (
            <div style={{ padding: "2rem", textAlign: "center" }}>
              <h1>A new version is available</h1>
              <p>Reload to continue.</p>
              <button onClick={() => window.location.reload()}>Reload</button>
            </div>
          );
        }
        return (
          <div style={{ padding: "2rem", textAlign: "center" }}>
            <h1>Something went wrong</h1>
            <p>We've been notified and will look into it.</p>
            <pre
              style={{
                textAlign: "left",
                background: "#f5f5f5",
                padding: "1rem",
              }}
            >
              {error instanceof Error
                ? error.message
                : "An unknown error occurred"}
            </pre>
            <button onClick={resetError}>Try again</button>
          </div>
        );
      }}
    >
      <QueryClientProvider client={queryClient}>
        <AssumedRoleProvider>
          <MantineProvider theme={theme} defaultColorScheme="auto">
            {/* SCRUM-175 follow-up: mutation/query error toasts (US-081) must
                stay visible above modals lifted for stacking above the base
                modals.open() (zIndex 200) — several go to 1000, with their own
                comboboxes/popovers going to 1100 (see AssignProviderFormsModal,
                ArtifactReturnModal, etc.). Notifications previously used
                Mantine's default (~400), so a rejected mutation's error toast
                rendered BEHIND any lifted modal instead of on top of it. */}
            <Notifications position="top-right" zIndex={1200} />
            <SentryAuthSync />
            <CsrfInitializer />
            <NativeSessionGuard />
            <DesktopAppEvents />
            {/*
              ModalsProvider MUST be inside BrowserRouter — it (or its children)
              uses router context via useNavigate / useLocation. Hoisting it above
              the router would crash with "useNavigate() may be used only in the
              context of a <Router> component".
            */}
            <BrowserRouter>
              {/* a11y: give every modals.open() close (✕) button an accessible
                  name (Mantine's CloseButton has none by default). */}
              <ModalsProvider
                modalProps={{
                  closeButtonProps: { "aria-label": "Close dialog" },
                }}
              >
                {/* US-183: blocks ALL app content (including the login
                    screen — the backend gate fires before CSRF/auth, so
                    even /api/auth/csrf on a stale iOS build can trip it)
                    behind a full-screen message once any API call reports
                    the running binary is below the configured minimum. */}
                <AppUpdateRequiredGate>
                  <AppRoutes />
                </AppUpdateRequiredGate>
              </ModalsProvider>
            </BrowserRouter>
            <ReactQueryDevtools initialIsOpen={false} />
          </MantineProvider>
        </AssumedRoleProvider>
      </QueryClientProvider>
    </Sentry.ErrorBoundary>
  );
}

export default App;
